import type { CatalogPlayer, CatalogTeamBlock, ProjectState } from "@livcg/schema";
import { useState } from "react";
import { Button, cn, ModalShell } from "../../ui/index.js";
import { byNumber } from "../rosterOrder.js";
import { useMatchDetail } from "../useMatchDetail.js";
import { pitchState } from "./pitchState.js";
import { type SubPair, useSubDraft } from "./subDraft.js";

const MAX_PAIRS = 5;

function onField(block: CatalogTeamBlock | undefined): CatalogPlayer[] {
  const roster = block?.roster ?? [];
  if (roster.length > 0)
    return roster
      .filter((r) => r.isStarter)
      .sort(byNumber)
      .map((r) => ({ ...r, id: r.playerId }));
  return [...(block?.players ?? [])].sort(byNumber);
}
function bench(block: CatalogTeamBlock | undefined): CatalogPlayer[] {
  const roster = block?.roster ?? [];
  return roster
    .filter((r) => !r.isStarter)
    .sort(byNumber)
    .map((r) => ({ ...r, id: r.playerId }));
}
const nm = (p: CatalogPlayer) => (p.lastName || p.firstName).trim();

export function SubstitutionModal({
  slug, state, side, open, onClose, onFire,
}: {
  slug: string; state: ProjectState; side: "home" | "away";
  open: boolean; onClose: () => void; onFire: (side: "home" | "away", pairs: SubPair[]) => void;
}) {
  const md = useMatchDetail(state);
  const [outId, setOutId] = useState<string | null>(null);
  const [inId, setInId] = useState<string | null>(null);
  // Saved in the browser — survives closing the modal and a page reload.
  const [draft, setPairs] = useSubDraft(slug, side);
  if (!open) return null;

  const block = side === "home" ? md.detail?.home : md.detail?.away;
  // Substitutions already logged are replayed: a substitute who came on can
  // be taken off again, a player who went off is not offered again.
  const pitch = pitchState(onField(block), bench(block), md.detail?.events ?? [], block?.team.id);
  const field = pitch.onField.sort(byNumber);
  // A sent-off player can neither go off nor come on.
  const sentOff = (id: string) => !!state.match.playerCards?.[id]?.sentOff;
  // A saved pair goes stale if, meanwhile, its player went off or was sent off.
  const pairs = md.detail
    ? draft.filter(
        (p) =>
          field.some((f) => f.id === p.outId) &&
          pitch.available.some((b) => b.id === p.inId) &&
          !sentOff(p.outId) &&
          !sentOff(p.inId),
      )
    : [];
  const usedOut = new Set(pairs.map((p) => p.outId));
  const usedIn = new Set(pairs.map((p) => p.inId));
  const outList = field.filter((p) => !usedOut.has(p.id) && !sentOff(p.id));
  const inList = pitch.available.filter((p) => !usedIn.has(p.id) && !sentOff(p.id));

  const addPair = () => {
    if (!outId || !inId || pairs.length >= MAX_PAIRS) return;
    const o = field.find((p) => p.id === outId);
    const i = pitch.available.find((p) => p.id === inId);
    if (!o || !i) return;
    setPairs([
      ...pairs,
      { outId: o.id, outName: nm(o), outNumber: o.number, inId: i.id, inName: nm(i), inNumber: i.number },
    ]);
    setOutId(null);
    setInId(null);
  };

  const fire = () => {
    if (pairs.length === 0) return;
    onFire(side, pairs);
    setPairs([]);
    onClose();
  };

  return (
    <ModalShell onClose={onClose}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-ink">
            Almashinuv · {block?.team.shortName ?? "—"} ({side === "home" ? "Home" : "Away"})
          </h3>
          <Button variant="ghost" size="xs" onClick={onClose}>✕</Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-md border border-divider p-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">Chiquvchi (maydonda)</div>
            <div className="flex max-h-56 flex-col gap-px overflow-y-auto">
              {outList.map((p) => {
                const sel = outId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setOutId(p.id)}
                    className={cn(
                      "flex items-center rounded px-2 py-1 text-left text-[12px] transition-colors",
                      sel ? "bg-live font-semibold text-white shadow-sm" : "hover:bg-surface-hover",
                    )}
                  >
                    <span className={cn("mr-1 inline-block w-6 text-right font-mono text-[10.5px]", sel ? "text-white/80" : "text-ink-faint")}>{p.number ?? "—"}</span>
                    {nm(p)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-md border border-divider p-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">Kiruvchi (zaxira)</div>
            <div className="flex max-h-56 flex-col gap-px overflow-y-auto">
              {inList.map((p) => {
                const sel = inId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setInId(p.id)}
                    className={cn(
                      "flex items-center rounded px-2 py-1 text-left text-[12px] transition-colors",
                      sel ? "bg-success font-semibold text-white shadow-sm" : "hover:bg-surface-hover",
                    )}
                  >
                    <span className={cn("mr-1 inline-block w-6 text-right font-mono text-[10.5px]", sel ? "text-white/80" : "text-ink-faint")}>{p.number ?? "—"}</span>
                    {nm(p)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-2 flex justify-center">
          <Button variant="secondary" size="sm" onClick={addPair} disabled={!outId || !inId || pairs.length >= MAX_PAIRS}>
            + Juftlik qo'shish
          </Button>
        </div>

        {pairs.length > 0 && (
          <div className="mt-2 rounded-md border border-accent-strong/40 bg-surface-muted/40 p-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Tayyor almashinuvlar ({pairs.length}/{MAX_PAIRS}) · saqlangan
            </div>
            {pairs.map((p, i) => (
              <div key={p.outId} className="flex items-center justify-between gap-2 py-0.5 text-[12px] text-ink">
                <span>#{p.outNumber ?? "—"} {p.outName} → #{p.inNumber ?? "—"} {p.inName}</span>
                <button type="button" className="text-ink-faint hover:text-live" onClick={() => setPairs(pairs.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <Button variant="success" size="sm" className="w-full uppercase" onClick={fire} disabled={pairs.length === 0}>
            Efirga ({pairs.length})
          </Button>
          <Button variant="secondary" size="sm" className="w-full" onClick={onClose}>Yopish</Button>
        </div>
    </ModalShell>
  );
}
